/* eslint-disable import/no-unresolved */
/* eslint-disable radix */
import { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import {partition, isEmpty} from 'lodash';
import {
  Grid,
  IconButton,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { makeStyles } from 'tss-react/mui';

import Navigation from './Navigation';
import GreenButton from '../buttons/Green';
import { BookingApi } from '../../client_http_api';
import { prepareTimesArray } from '../../shared_client_utils/dateUtils';
import {
  formatDate,
  formatTime,
} from '../../shared_client_utils/bookingUtils';
import DatePicker from '../DatePickerInput';
import { setHoursAndMinutesForDate } from '../../shared_client_utils/momentUtils';

const useStyles = makeStyles()((theme) => ({
  main: {
    padding: theme.spacing(2, 2, 0, 2),
    backgroundColor: '#ffffff',
  },
  mainCalendarAreaBoxWhenNotSelected: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  calendarBox: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0
  },
  calendar: {
    border: 'none',
    width: '100%',

    '& > .react-datepicker__month-container': {
      width: '100%',

      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 2),
      },

      '& > .react-datepicker__header': {
        backgroundColor: '#ffffff',
        borderBottom: 'none',
        paddingTop: 0,

        '& .react-datepicker__day-name': {
          fontSize: '18px',
          margin: '0.4rem',
          color: grey[600],
        },
      },

      '& > .react-datepicker__month': {
        margin: 0,
      },

      '& .react-datepicker__week, & .react-datepicker__day-names': {
        display: 'flex',
        justifyContent: 'space-between',
      },

      '& .react-datepicker__day-name, & .react-datepicker__day': {
        width: '2.2rem',
        lineHeight: '2.2rem',
      },
    },
  },
  calendarDay: {
    fontSize: '18px',
    margin: '0.4rem',
    outline: 'none',
  },
  calendarHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  calendarCurrentMonth: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  dayNotSelected: {
    display: 'flex',
    justifyContent: 'center',
    fontStyle: 'italic',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1
  },
  timesBox: {
    justifyContent: 'space-evenly',
    marginTop: theme.spacing(2),
  },
  times: {
    width: '40%',
    flexDirection: 'column',
  },
  noTimes: {
    fontSize: '14px',
    color: grey[500],
    marginTop: '10px',
  },
  formControlLabelRoot: {
    marginLeft: 0,
    marginRight: 0,
  },
  formControlLabelLabel: {
    width: '100%',
  },
  radioRoot: {
    paddingLeft: 0,
  },
  timesTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  checkboxLabel: {
    position: 'relative',
  },
  beginTime: {
    fontSize: theme.spacing(2),
  },
  endTime: {
    position: 'absolute',
    bottom: theme.spacing(-2),
    fontSize: '14px',
    color: grey[600],
  },
}));
const timeOptions = prepareTimesArray();
const findTimeOptionLabel = (time) => {
  return timeOptions.find(({ value }) => value === time).label;
};

const afternoonTime = 720;

const prepareDatetimeOptions = (options) => {
  const { date, beginTime, endTime, moment, timezone } = options;

  // The date that the client's datepicker gives in its time zone. For our backend, we create dates taking into account work in the business time zone.
  const dateMill = date.getTime();
  const selectedDate = moment.tz(dateMill, timezone);
  selectedDate.date(date.getDate()).month(date.getMonth()).year(date.getFullYear());

  const startOfDay = selectedDate.startOf('day');
  const startDatetime = setHoursAndMinutesForDate(startOfDay, beginTime, {
    moment,
  });
  const endDatetime = setHoursAndMinutesForDate(startOfDay, endTime, {
    moment,
  });

  return {
    timezone,
    date: selectedDate.toDate(),
    beginTime,
    endTime,
    startDatetime,
    endDatetime,
    formattedDate: formatDate(startDatetime),
    formattedStartTime: formatTime(startDatetime),
    formattedStopTime: formatTime(endDatetime),
  };
};

const renderCustomHeader = (classes) => (props) => {
  const {
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
  } = props;

  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();

  return (
    <Grid container className={classes.calendarHeader}>
      <IconButton
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        <NavigateBeforeIcon />
      </IconButton>
      <Grid item className={classes.calendarCurrentMonth}>
        {`${month} ${year}`}
      </Grid>
      <IconButton
        onClick={increaseMonth}
      >
        <NavigateNextIcon />
      </IconButton>
    </Grid>
  );
};

const dateToShortString = date => date.toLocaleDateString('en-GB')
const dateToMonthString = date => `${date.getMonth() + 1}-${date.getFullYear()}`

const DateTime = (props) => {
  const {
    classes: parentClasses,
    business,
    currentStep,
    stepsData,
    handleStartLoading,
    handleStopLoading,
    isChangeAppointment,
    handleDisplayFlashMessage,
    handleUpdateBooking,
    handleNextStep,
    client,
    setTempUnavailableId,
    tempUnavailableId,
    setUnloadListener,
    unloadListener
  } = props;

  const {classes, cx} = useStyles();
  const currentStepData = currentStep.getData(stepsData);

  const loadedMonthsSetRef = useRef(new Set())
  const [availableDates, setAvailableDates] = useState([])
  const currentMonthStringRef = useRef(dateToMonthString(new Date()))
  const [dateTime, setDateTime] = useState(() => ({}));

  const bookingTimeReservation = async timeObject => {
    const data = {...dateTime, ...timeObject}
    const dateTimeOptions = prepareDatetimeOptions({
      moment,
      date: data.date,
      beginTime: data.beginTime,
      endTime: data.endTime,
      timezone: business.timezone,
    });
    const params = {
      businessId: business?.id,
      staffId: stepsData?.staff?.id && !isEmpty(stepsData?.staff?.id) ? stepsData?.staff?.id : data.staffId,
      serviceId: stepsData?.service?.id,
      locationId: stepsData?.location?.id,
      dateTimeOptions,
      client,
      clientCurrentTime: new Date().toString(),
      tempUnavailableId,
      timezone: business.timezone,
    }
    try {
      handleStartLoading()
      const result = await BookingApi.createTempUnavailableTime(params)
      const newTempUnavailableId = result?.tempUnavailableId;
      if (newTempUnavailableId) {
        setTempUnavailableId(newTempUnavailableId);
        if (unloadListener) {
          window.removeEventListener('beforeunload', unloadListener);
        }
        const handler = () => {
          const data = JSON.stringify({});
          navigator.sendBeacon(`${window.baseApiPath}/booking/delete-temp-unavailable-time/${newTempUnavailableId}`, data);
        };
        setUnloadListener(handler);
        window.addEventListener('beforeunload', handler);
      }
      handleStopLoading();
      return true;
    } catch (error) {
      handleStopLoading();
      return true;
    }
  }

  const [morningTimes, setMorningTimes] = useState([]);
  const handlePickMorningTime = async ({ target: { value } }) => {
    const morningBeginTime = parseInt(value);
    const timeObject = morningTimes.find((timeObject) => {
      return timeObject.beginTime === morningBeginTime;
    });
    setDateTime((prevValue) => ({
      ...prevValue,
      ...timeObject,
    }));
    await bookingTimeReservation(timeObject)
  };

  const [afternoonTimes, setAfternoonTimes] = useState([]);
  const handlePickAfternoonTime = async ({ target: { value } }) => {
    const afternoonBeginTime = parseInt(value);
    const timeObject = afternoonTimes.find((timeObject) => {
      return timeObject.beginTime === afternoonBeginTime;
    });

    setDateTime((prevValue) => ({
      ...prevValue,
      ...timeObject,
    }));
    await bookingTimeReservation(timeObject)
  };

  const fetchTimes = async ({startDate}) => {
    const monthString = dateToMonthString(startDate.toDate())
    const businessTimezone = business.timezone;
    let staffId = '';
    if (!stepsData.staff.isNoPreference) {
      staffId = stepsData.staff.id;
    }
    const lastDayOfMonth = moment(startDate).endOf('month')
    const daysToEndOfMonth = lastDayOfMonth.diff(startDate, 'days')
    // Calendar shows days of the next week up to the end of the week
    const daysToEndOfLastWeek = moment(lastDayOfMonth).endOf('week').diff(lastDayOfMonth, 'days')
    const requestData = {
      startDate: startDate.toISOString(),
      businessTimezone,
      staffId,
      locationId: stepsData.location.id,
      serviceId: stepsData.service.id,
      businessId: business.id,
      days: daysToEndOfMonth + 1 + daysToEndOfLastWeek
    }
    handleStartLoading()
    const dates = await BookingApi.fetchNearestFreeDayTimes(requestData)
    setAvailableDates(prevAvailableDates => prevAvailableDates
      .concat(dates.map(({d: availableDate, t: times}) => {
        // We create an array of dates for the datepicker in the client’s timezone to correctly display free time in the business timezone.
        const date = new Date(availableDate.replace(/\+.*/, ''));
        const timezoneOffset = date.getTimezoneOffset();
        date.setMinutes(date.getMinutes() + timezoneOffset);
        const dateStr = dateToShortString(date)
        return ({date, times, dateStr})
      })))
    loadedMonthsSetRef.current.add(monthString)
    handleStopLoading()
  }

  const start = async () => {
    if (unloadListener) {
      window.removeEventListener('beforeunload', unloadListener);
      setUnloadListener(null);
      if (tempUnavailableId) {
        await BookingApi.deleteTempUnavailableTime({tempUnavailableId});
        setTempUnavailableId(null);
      }
    }
    await fetchTimes({startDate: moment.tz(business.timezone)})
  }

  useEffect(() => {
    start();
  }, [business]);

  const loadDatesForMonthDate = date => {
    const monthString = dateToMonthString(date)
    if (loadedMonthsSetRef.current.has(monthString)) return
    const firstDayOfMonth = moment(date).startOf('month')
    // Don't load days that were already loaded when we loaded prev month
    // (calendar shows next month days if month doesn't end on Saturday)
    // NOTE: calendar shows Sunday as the first day, not Monday
    const startDate = (firstDayOfMonth.weekday() === 0 ? firstDayOfMonth : firstDayOfMonth.weekday(7)).startOf('day')
    fetchTimes({startDate})
  }

  const handleChangeDate = date => {
    setDateTime((prevValue) => ({
      ...prevValue,
      date,
      beginTime: null,
      endTime: null,
    }));
    const dateStr = dateToShortString(date)
    const {times} = availableDates.find(ad => ad.dateStr === dateStr) || {}
    if (!times) return

    const formattedTimes = times.map(({ beginTime, endTime, ...timeObject }) => ({
      ...timeObject,
      beginTime,
      endTime,
      formattedBeginTime: findTimeOptionLabel(beginTime),
      formattedEndTime: findTimeOptionLabel(endTime),
    }));
    const [
      morningTimes,
      afternoonTimes,
    ] = partition(formattedTimes, ({ beginTime }) => beginTime < afternoonTime);
    setMorningTimes(morningTimes);
    setAfternoonTimes(afternoonTimes);

    // Calendar can switch to next month when you click on last dates (an if those dates are of next month) but
    // it doesn't trigger onMonthChange callback. So we load dates for next month manually
    if (currentMonthStringRef.current !== dateToMonthString(date)) {
      loadDatesForMonthDate(date)
    }
  };

  const handleMonthChanged = date => {
    const monthString = dateToMonthString(date)
    currentMonthStringRef.current = monthString
    loadDatesForMonthDate(date)
  }

  const onNextStepClick = async () => {
    if (!dateTime.beginTime) {
      handleDisplayFlashMessage('Pick a time', 'error');
      return;
    }

    const datetimeOptions = prepareDatetimeOptions({
      moment,
      date: dateTime.date,
      beginTime: dateTime.beginTime,
      endTime: dateTime.endTime,
      timezone: business.timezone,
    });

    const newStepData = {
      datetime: {
        ...currentStepData,
        ...datetimeOptions,
      },
      staff: {
        ...stepsData.staff,
        id: dateTime.staffId,
        name: dateTime.staffAlias,
      },
    };
    if (dateTime.resourceItemId) {
      newStepData.service = {
        ...stepsData.service,
        resourceItemId: dateTime.resourceItemId,
      };
    }
    const checkParams = {
      businessId: business?.id,
      staffId: newStepData?.staff?.id,
      locationId: stepsData?.location?.id,
      dateTimeOptions: datetimeOptions,
      availableDateOptions: dateTime,
      client,
      clientCurrentTime: new Date().toString(),
      isNoPreference: stepsData?.staff?.isNoPreference,
    }

    handleStartLoading()
    try {
      await BookingApi.checkAvailableDates(checkParams)
      handleStopLoading()
      if (isChangeAppointment) {
        handleUpdateBooking(newStepData);
      } else {
        handleNextStep(newStepData);
      }
    } catch (error) {
      handleDisplayFlashMessage(error.message || 'Unexpected error, try again', 'error');
      await fetchTimes({startDate: moment.tz(business.timezone)})
      handleStopLoading()
    }
  };
  const isDateSelected = Boolean(dateTime.date)
  const enabledDates = availableDates.map(ad => ad.date)

  useEffect(() => {
    // Select the first available date on mount
    if (availableDates?.length > 0 && !dateTime?.date) {
      handleChangeDate(availableDates[0].date)
    }
  }, [availableDates])

  return (
    <div className={parentClasses.grid}>
      <Navigation {...props} />
      <main className={cx(parentClasses.main, classes.main)}>
        <Grid container className={isDateSelected ? undefined : classes.mainCalendarAreaBoxWhenNotSelected}>
          <Grid item className={classes.calendarBox}>
            <DatePicker
              inline
              id="date"
              selected={dateTime.date}
              onChange={handleChangeDate}
              minDate={moment().toDate()}
              calendarClassName={classes.calendar}
              everyDayClassName={classes.calendarDay}
              renderCustomHeader={renderCustomHeader(classes)}
              includeDates={enabledDates}
              // filterDate={filterDate}
              onMonthChange={handleMonthChanged}
            />
          </Grid>
          {isDateSelected ? (
            <Grid container className={classes.timesBox}>
              <Grid container className={classes.times}>
                <Grid item className={classes.timesTitle}>Morning</Grid>
                {isEmpty(morningTimes) ? (
                  <Grid item className={classes.noTimes}>
                    No times available
                  </Grid>
                ) : (
                  <FormControl>
                    <RadioGroup
                      name="time"
                      value={dateTime.beginTime}
                    >
                      {morningTimes.map((morningTime) => (
                        <FormControlLabel
                          key={morningTime.beginTime}
                          name={String(morningTime.beginTime)}
                          value={morningTime.beginTime}
                          onChange={handlePickMorningTime}
                          control={(
                            <Radio
                              color="primary"
                              classes={{
                                root: classes.radioRoot,
                              }}
                              component="div"
                            />
                          )}
                          label={(
                            <Grid
                              container
                              className={classes.checkboxLabel}
                            >
                              <Grid item className={classes.beginTime}>
                                {morningTime.formattedBeginTime}
                              </Grid>
                              {morningTime.beginTime === dateTime.beginTime && (
                                <Grid item className={classes.endTime}>
                                  ends {morningTime.formattedEndTime}
                                </Grid>
                              )}
                            </Grid>
                          )}
                          classes={{
                            root: classes.formControlLabelRoot,
                            label: classes.formControlLabelLabel,
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </Grid>
              <Grid container className={classes.times}>
                <Grid item className={classes.timesTitle}>Afternoon</Grid>
                {isEmpty(afternoonTimes) ? (
                  <Grid item className={classes.noTimes}>
                    No times available
                  </Grid>
                ) : (
                  <FormControl>
                    <RadioGroup
                      name="time"
                      value={dateTime.beginTime}
                    >
                      {afternoonTimes.map((afternoonTime) => (
                        <FormControlLabel
                          key={afternoonTime.beginTime}
                          name={String(afternoonTime.beginTime)}
                          value={afternoonTime.beginTime}
                          onChange={handlePickAfternoonTime}
                          control={(
                            <Radio
                              color="primary"
                              classes={{
                                root: classes.radioRoot,
                              }}
                              component="div"
                            />
                          )}
                          label={(
                            <Grid
                              container
                              className={classes.checkboxLabel}
                            >
                              <Grid item className={classes.beginTime}>
                                {afternoonTime.formattedBeginTime}
                              </Grid>
                              {afternoonTime.beginTime === dateTime.beginTime && (
                                <Grid item className={classes.endTime}>
                                  ends {afternoonTime.formattedEndTime}
                                </Grid>
                              )}
                            </Grid>
                          )}
                          classes={{
                            root: classes.formControlLabelRoot,
                            label: classes.formControlLabelLabel,
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid container className={classes.dayNotSelected}>
              Select a day
            </Grid>
          )}
        </Grid>
      </main>

      <footer className={parentClasses.footer}>
        <GreenButton
          stretched
          variant="contained"
          size="large"
          onClick={onNextStepClick}
          className={parentClasses.mainButton}
        >
          {isChangeAppointment ? 'Update' : 'Next'}
        </GreenButton>
      </footer>
    </div>
  );
};

export default DateTime;
